/**
 * Client for the unified integrations manager (Plan C 2.1 + 2.3).
 *
 * THE INVARIANT THIS FILE ENCODES IN ITS TYPES
 * --------------------------------------------
 * A credential value travels ONE way. `SecretValueIn` (request) carries
 * values; `SecretStatus` (response) has no field that could hold one, so
 * "the browser cannot read back a stored secret" is a property of the
 * type, not a habit. There is deliberately no `getSecret`, and no
 * `values` field on any response interface — a vitest asserts that.
 *
 * The consequence in the UI: a credential form never pre-fills. It shows
 * "configured · ****1234" and offers REPLACE.
 *
 * The catalog (per-channel fields + the numbered steps the customer must
 * perform in THEIR system) is fetched, not hardcoded here. It lives once,
 * in `builder/integrations_catalog.py`, localised server-side — a second
 * copy in TypeScript is how one of them ends up naming a menu item that
 * Microsoft renamed.
 */

import { api } from "@/lib/api";

export type SecretSource = "managed" | "env" | "none";

/** Everything the UI may know about a stored value. NO value field. */
export interface SecretStatus {
  source: SecretSource;
  configured: boolean;
  /** Last four chars; EMPTY under 8 chars and always empty for `env`. */
  last4: string;
  version: number | null;
  updated_by: string | null;
  updated_at: string | null;
  backend: string;
  /** For `env` only: the exact variable an operator must set. */
  env_var: string;
  /** The backend's own actionable message when the status is unknown. */
  detail: string;
}

export interface CredentialUsage {
  kind: "subscription" | "project";
  id: string;
  label: string;
}

export interface ChannelCredential {
  id: string;
  name: string;
  channel: string;
  /** A POINTER. Public by design — it names a path, never a value. */
  secret_ref: string;
  source: SecretSource;
  description: string;
  secret: SecretStatus;
  used_by: CredentialUsage[];
  created_at: string;
  updated_at: string;
}

export interface ManagedConnection {
  id: string;
  name: string;
  kind: string;
  config: Record<string, unknown>;
  secret_ref: string;
  /** `null` ⇒ SHARED with every project in this tenant. */
  project_id: string | null;
  project_slug: string | null;
  /** Migration 035 — projects this OWNED connection is also granted to.
      Never includes the owner; always empty when tenant-wide. */
  shared_project_ids: string[];
  shared_project_slugs: string[];
  secret: SecretStatus;
  used_by: CredentialUsage[];
  created_at: string;
  updated_at: string;
}

export interface CatalogField {
  key: string;
  label: string;
  help: string;
  type: "text" | "password" | "number" | "url" | "email";
  required: boolean;
  placeholder: string;
  secret: boolean;
}

export interface ExternalGuide {
  where: string;
  intro: string;
  steps: string[];
  warnings: string[];
  doc_url: string;
}

export interface CatalogCard {
  /** Present on channel cards. */
  channel?: string;
  /** Present on data-source cards. */
  kind?: string;
  label: string;
  summary: string;
  value_format: "raw" | "json";
  fields: CatalogField[];
  guide: ExternalGuide | null;
  /**
   * A guide that already exists as a COMPONENT, named instead of
   * duplicated. Only `"teams_workflow"` today: the Teams steps shipped
   * in Plan C fase 5 with their strings in the i18n dictionaries, so the
   * server sends a pointer rather than a second copy of Microsoft's
   * menu path.
   */
  guide_component: string;
  /** false ⇒ render WITHOUT a form and say why (WhatsApp). */
  available: boolean;
  unavailable_reason: string | null;
}

export interface IntegrationsCatalog {
  locale: string;
  channels: CatalogCard[];
  sources: CatalogCard[];
  implemented_channels: string[];
  secret_backend_configured: boolean;
  /** Numbered steps to enable the writable backend, served verbatim. */
  secret_backend_setup: string[];
}

export interface IntegrationTestResult {
  ok: boolean;
  detail: Record<string, unknown>;
  message: string;
}

const BASE = "/api/builder/integrations";

export const integrationsApi = {
  catalog: (locale: string) =>
    api.get<IntegrationsCatalog>(
      `${BASE}/catalog?locale=${encodeURIComponent(locale)}`,
    ),

  channels: {
    /**
     * `locale` is not cosmetic here: the row's `secret.detail` is OUR
     * copy (e.g. "the variable X is not set on this container") and ships
     * EN + ES. An unknown locale is a 422, never a silent English row.
     */
    list: (locale: string) =>
      api.get<{ credentials: ChannelCredential[]; total: number }>(
        `${BASE}/channels?locale=${encodeURIComponent(locale)}`,
      ),
    create: (payload: {
      name: string;
      channel: string;
      description: string;
    }) => api.post<ChannelCredential>(`${BASE}/channels`, payload),
    update: (id: string, payload: { description: string }) =>
      api.put<ChannelCredential>(`${BASE}/channels/${id}`, payload),
    /**
     * Store or REPLACE the value. Returns the credential WITHOUT it.
     * There is no counterpart that reads it back — by design.
     */
    replaceSecret: (id: string, values: Record<string, string>) =>
      api.put<ChannelCredential>(`${BASE}/channels/${id}/secret`, { values }),
    test: (id: string) =>
      api.post<IntegrationTestResult>(`${BASE}/channels/${id}/test`),
    remove: (id: string) => api.delete<void>(`${BASE}/channels/${id}`),
  },

  connections: {
    list: (locale: string) =>
      api.get<{ connections: ManagedConnection[]; total: number }>(
        `${BASE}/connections?locale=${encodeURIComponent(locale)}`,
      ),
    createShared: (payload: {
      name: string;
      kind: string;
      config: Record<string, unknown>;
      secret_ref: string;
    }) => api.post<ManagedConnection>(`${BASE}/connections`, payload),
    /** `project_id: null` shares it with every project in this tenant. */
    setScope: (id: string, projectId: string | null) =>
      api.put<ManagedConnection>(`${BASE}/connections/${id}/scope`, {
        project_id: projectId,
      }),
    /** The FULL set of extra projects an owned connection is granted to
        (replaces, never appends). 409 names any project whose data
        source still relies on a grant being revoked. */
    setProjects: (id: string, projectIds: string[]) =>
      api.put<ManagedConnection>(`${BASE}/connections/${id}/projects`, {
        project_ids: projectIds,
      }),
    replaceSecret: (id: string, values: Record<string, string>) =>
      api.put<ManagedConnection>(`${BASE}/connections/${id}/secret`, {
        values,
      }),
    test: (id: string) =>
      api.post<IntegrationTestResult>(`${BASE}/connections/${id}/test`),
    remove: (id: string) => api.delete<void>(`${BASE}/connections/${id}`),
  },
};
